import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './components/auth/LoginPage';
import RegisterPage from './components/auth/RegisterPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import CandidateUpload from './components/candidate/CandidateUpload';
import FilterPage from './components/filter/FilterPage';
import { HistoricalPage } from './components/historical';
import { HelpPage } from './components/help';
import AdminPage from './components/admin/AdminPage';
import { BillingPage } from './components/billing';
import SurveyPage from './components/survey/SurveyPage';
import SurveyEditor from './components/survey/SurveyEditor';
import SurveyPublicView from './components/survey/SurveyPublicView';
import SurveyLandingPage from './components/survey/SurveyLandingPage';
import { useAuth } from './hooks/useAuth';
import StagingBanner from './components/StagingBanner';

const isSurveyDomain = () =>
  ['zapojsedovoleb.cz', 'www.zapojsedovoleb.cz'].includes(window.location.hostname);

// Loading Spinner Component with Phoenix design
const LoadingSpinner = () => (
  <div
    className="min-h-screen flex items-center justify-center"
    style={{ background: 'linear-gradient(135deg, var(--phoenix-700) 0%, var(--phoenix-900) 100%)' }}
  >
    <div className="text-center">
      {/* Logo */}
      <div
        className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 text-white"
        style={{
          background: 'linear-gradient(135deg, var(--phoenix-400) 0%, var(--phoenix-500) 100%)',
          boxShadow: '0 8px 24px rgba(37, 99, 235, 0.3)'
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-8 h-8">
          <path d="M12 2L2 7l10 5 10-5-10-5z"/>
          <path d="M2 17l10 5 10-5"/>
          <path d="M2 12l10 5 10-5"/>
        </svg>
      </div>

      {/* Spinner */}
      <div className="w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4" />

      {/* Text */}
      <p className="text-white/80 font-medium">Načítám...</p>
    </div>
  </div>
);

// Pending Approval Page Component
const PendingApprovalPage = ({ onSignOut }: { onSignOut: () => void }) => (
  <div
    className="min-h-screen flex items-center justify-center p-8"
    style={{ background: 'linear-gradient(135deg, var(--phoenix-700) 0%, var(--phoenix-900) 100%)' }}
  >
    {/* Decorative circles */}
    <div className="absolute w-[600px] h-[600px] top-[-200px] right-[-100px] rounded-full pointer-events-none"
      style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
    <div className="absolute w-[400px] h-[400px] bottom-[-100px] left-[-50px] rounded-full pointer-events-none"
      style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />

    <div
      className="w-full max-w-md p-12 rounded-3xl relative z-10 text-center"
      style={{ background: 'var(--bg-secondary)', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
    >
      {/* Warning Icon */}
      <div
        className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
        style={{ background: 'var(--warning-100)' }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="var(--warning-600)" strokeWidth="2" className="w-10 h-10">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      </div>

      <h2 className="font-display text-2xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
        Čeká na schválení
      </h2>

      <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
        Váš účet byl úspěšně vytvořen a čeká na schválení administrátorem.
      </p>

      <div
        className="p-4 rounded-xl mb-6"
        style={{ background: 'var(--warning-50)', border: '1px solid var(--warning-200)' }}
      >
        <p className="text-sm" style={{ color: 'var(--warning-700)' }}>
          Jakmile bude váš účet schválen, budete moci plně využívat aplikaci Phoenix Analytics.
        </p>
      </div>

      <button
        onClick={onSignOut}
        className="w-full py-3 px-4 rounded-xl font-medium transition-all"
        style={{
          background: 'var(--bg-tertiary)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border-light)'
        }}
      >
        Odhlásit se
      </button>
    </div>
  </div>
);

// Rejected Page Component
const RejectedPage = ({ onSignOut }: { onSignOut: () => void }) => (
  <div
    className="min-h-screen flex items-center justify-center p-8"
    style={{ background: 'linear-gradient(135deg, var(--phoenix-700) 0%, var(--phoenix-900) 100%)' }}
  >
    <div
      className="w-full max-w-md p-12 rounded-3xl relative z-10 text-center"
      style={{ background: 'var(--bg-secondary)', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
    >
      {/* Error Icon */}
      <div
        className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
        style={{ background: 'var(--danger-100)' }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="var(--danger-600)" strokeWidth="2" className="w-10 h-10">
          <circle cx="12" cy="12" r="10"/>
          <line x1="15" y1="9" x2="9" y2="15"/>
          <line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
      </div>

      <h2 className="font-display text-2xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
        Přístup zamítnut
      </h2>

      <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
        Váš účet nebyl schválen administrátorem. Pokud si myslíte, že jde o chybu, kontaktujte podporu.
      </p>

      <button
        onClick={onSignOut}
        className="w-full py-3 px-4 rounded-xl font-medium transition-all"
        style={{
          background: 'var(--bg-tertiary)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border-light)'
        }}
      >
        Odhlásit se
      </button>
    </div>
  </div>
);

function App() {
  const { user, loading, isAdmin, isApproved, userStatus, signOut } = useAuth();

  // Survey domain: show only survey routes, no Phoenix branding
  if (isSurveyDomain()) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<SurveyLandingPage />} />
          <Route path="/:surveyId" element={<SurveyPublicView />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Debug logging
  console.log('[App] Auth state:', {
    user: user?.email,
    loading,
    isAdmin,
    isApproved,
    userStatus
  });

  if (loading) {
    return <LoadingSpinner />;
  }

  // Email verification disabled - users pay for credits so they have incentive to use real emails

  // User is logged in but not approved - show pending/rejected page
  if (user && !isApproved) {
    if (userStatus === 'rejected') {
      return <RejectedPage onSignOut={signOut} />;
    }
    return <PendingApprovalPage onSignOut={signOut} />;
  }

  return (
    <BrowserRouter>
      <StagingBanner />
      <Routes>
        {/* Public Routes */}
        <Route
          path="/"
          element={user ? <Navigate to="/filter" /> : <Navigate to="/login" />}
        />
        <Route
          path="/login"
          element={user ? <Navigate to="/filter" /> : <LoginPage />}
        />
        <Route
          path="/register"
          element={user ? <Navigate to="/filter" /> : <RegisterPage />}
        />
        <Route
          path="/forgot-password"
          element={user ? <Navigate to="/filter" /> : <ForgotPasswordPage />}
        />

        {/* Protected Routes - only for approved users */}
        <Route
          path="/filter"
          element={user && isApproved ? <FilterPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/historical"
          element={user && isApproved ? <HistoricalPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/candidate"
          element={user && isApproved ? <CandidateUpload /> : <Navigate to="/login" />}
        />
        <Route
          path="/help"
          element={user && isApproved ? <HelpPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/billing"
          element={user && isApproved ? <BillingPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/survey"
          element={user && isApproved ? <SurveyPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/survey/:id/edit"
          element={user && isApproved ? <SurveyEditor /> : <Navigate to="/login" />}
        />
        <Route
          path="/admin"
          element={user && isApproved ? <AdminPage /> : <Navigate to="/filter" />}
        />

        {/* Public Survey View - no authentication required */}
        {/* Matches UUID-like paths like /d7aa0d0e-f5ff-448a-a6a8-028cf7d4cac4 */}
        <Route path="/:surveyId" element={<SurveyPublicView />} />

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
